/**
 * DE-281 — how much should an operator trust the pin?
 *
 * The Portal used to answer that with one number: the age of `geo.timestamp`,
 * rendered as "53 d". That number is not what it looks like. `geo.timestamp` is
 * "position ESTABLISHED at", not "last confirmed at" — the resolver deliberately
 * carries the original fix's timestamp forward every time it re-affirms a
 * stationary device (lambda_function.py, both stationary paths), so a device
 * that never moves reads "365 d" however often it is verified.
 *
 * Meanwhile the shadow already carried the answer and the UI dropped it:
 * `last_attempt_at` / `last_attempt_status` say when the pipeline last ran and
 * what it concluded, `last_google_at` says whether that conclusion came from an
 * independent Google resolve or from the WiFi-fingerprint cache, and (since
 * DE-281) `cell_check` says whether an independent cell anchor corroborated the
 * WiFi. The card consumed the first two only on the FAILURE path, so a healthy
 * device showed none of it.
 *
 * The worked example this was written against: artomeui-113991334253500027
 * displayed "53 d" for a position Google had independently re-resolved that
 * morning to within 22 m, with 17 of 19 WiFi BSSIDs unchanged an hour later.
 *
 * Pure and clock-injectable so the three surfaces that render it (device card,
 * fleet-map hover card, location history) cannot word it differently.
 */
import type { GeoFix } from '../api/hooks';

/** Mirrors FP_CACHE_MAX_AGE_S in the geolocation resolver. Past this the
 *  fingerprint cache refuses to answer and a real Google call must happen. */
export const FP_CACHE_MAX_AGE_S = 24 * 3600;

/** Above this an untouched fix stops being "just confirmed" and we start
 *  asking why. Generous: the resolver throttles, devices publish a scan only
 *  when >3 BSSIDs change, and a parked device is genuinely quiet. */
export const STALE_ATTEMPT_S = 2 * 3600;

export type LocationConfidenceLevel =
  /** Re-affirmed recently by a successful attempt. */
  | 'confirmed'
  /** Current resolve failed; showing the last known position. */
  | 'unverified'
  /** Not checked lately, but the device has been offline — expected, not a fault. */
  | 'offline-stale'
  /** Not checked lately though the device is online — a real fault (cf. DE-280). */
  | 'stalled'
  /** Nothing to reason from (legacy fix, never scanned). */
  | 'unknown';

export type ConfidenceTone = 'ok' | 'muted' | 'warn';

export interface LocationConfidence {
  level: LocationConfidenceLevel;
  tone: ConfidenceTone;
  /** Primary line. Leads with confirmation, never with the fix age. */
  headline: string;
  /** Short corroboration chips, strongest first. May be empty. */
  evidence: string[];
  /** Demoted "established …" line, or null when there is no usable timestamp. */
  established: string | null;
  /** Long-form explanation for a tooltip / popup. */
  explanation: string;
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Epoch-seconds (the shadow's geo timestamps) or ISO → ms, else null. */
function toMs(ts: string | number | null | undefined): number | null {
  if (ts === null || ts === undefined) return null;
  if (typeof ts === 'number') return Number.isFinite(ts) ? (ts < 1e12 ? ts * 1000 : ts) : null;
  const parsed = Date.parse(ts);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Bare duration — "11 d". Reads correctly after a preposition, where the
 *  "ago" form does not ("no check in 11 d ago"). */
function span(ms: number, nowMs: number): string {
  const d = Math.max(0, (nowMs - ms) / 1000);
  if (d < 60) return `${Math.round(d)} s`;
  if (d < 3600) return `${Math.round(d / 60)} min`;
  if (d < 86400) return `${Math.round(d / 3600)} h`;
  return `${Math.round(d / 86400)} d`;
}

function ago(ms: number, nowMs: number): string {
  return `${span(ms, nowMs)} ago`;
}

/** "10 Sep", or "10 Sep 2025" once it is no longer the current year. Used where
 *  a second relative age would just repeat the first one.
 *
 *  Deliberately not toLocaleDateString: its abbreviations shift with the host's
 *  ICU version (Node 22 renders en-GB September as "Sept", browsers as "Sep"),
 *  which would make the same device read differently for two operators. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function shortDate(ms: number, nowMs: number): string {
  const d = new Date(ms);
  const label = `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
  return d.getUTCFullYear() === new Date(nowMs).getUTCFullYear()
    ? label
    : `${label} ${d.getUTCFullYear()}`;
}

/** Human wording for the DE-203 cross-check verdict. Only returns a chip when
 *  it tells the operator something — "we didn't check" on a cell-only fix is
 *  noise, not information. */
function cellEvidence(geo: GeoFix): string | null {
  const source = geo.source ?? '';
  const usedWifi = source.includes('wifi');
  // `source` is what the scan SENT, not what Google used. Since DE-334 the cell
  // really does contribute, so a wifi+cell fix that skipped the cross-check
  // must not be called "WiFi only" — it may well be the cell that placed it.
  const notChecked = source.includes('cell')
    ? 'WiFi + cell — not cross-checked'
    : 'WiFi only — not cross-checked';
  switch (geo.cell_check) {
    case 'agree':                  return 'cell-corroborated';
    case 'no-cell-fix':            return 'WiFi only — no cell anchor';
    case 'not-attempted':          return usedWifi ? notChecked : null;
    case 'wifi-rejected':          return 'WiFi rejected — showing cell position';
    case 'wifi-rejected-unusable': return 'WiFi rejected as implausible';
    default:                       return null;  // pre-DE-281 fix: unknown, don't guess
  }
}

export function locationConfidence(args: {
  geo: GeoFix | null | undefined;
  /** Backend-derived presence. Undefined when the caller has no device row. */
  online?: boolean;
  /** devices.last_seen — used to say how long it has been offline. */
  lastSeen?: string | null;
  nowMs?: number;
}): LocationConfidence {
  const { geo, online, lastSeen } = args;
  const nowMs = args.nowMs ?? Date.now();

  const established = toMs(geo?.timestamp ?? null) ?? toMs(geo?.last_verified_at ?? null);
  const establishedLabel = established === null
    ? null
    : `established ${ago(established, nowMs)}`;

  if (!geo) {
    return { level: 'unknown', tone: 'muted', headline: 'No location reported',
             evidence: [], established: null,
             explanation: 'This device has never reported a position.' };
  }

  const attemptMs = toMs(geo.last_attempt_at ?? null);
  const googleMs = toMs(geo.last_google_at ?? null);
  const status = geo.last_attempt_status ?? null;
  const failures = geo.consecutive_failures ?? 0;
  const currentVerified = isNum(geo.lat) && isNum(geo.lon);

  // Evidence is about the quality of the last SUCCESSFUL confirmation, so it
  // is assembled the same way whatever the level turns out to be.
  const evidence: string[] = [];
  if (googleMs !== null && attemptMs !== null) {
    // The resolver bumps last_google_at only when it actually called Google;
    // an fp-cache hit passes the old value through. Equal (within a minute)
    // ⇒ the latest attempt was the independent one.
    evidence.push(Math.abs(attemptMs - googleMs) <= 60_000
      ? 'independently verified'
      : `fingerprint match, last independent check ${ago(googleMs, nowMs)}`);
  }
  const cell = cellEvidence(geo);
  if (cell) evidence.push(cell);

  // ── current resolve failed ───────────────────────────────────────────
  if (!currentVerified) {
    const scans = failures > 0
      ? ` · ${failures} inconclusive scan${failures === 1 ? '' : 's'}${status ? ` (${status})` : ''}`
      : '';
    return {
      level: 'unverified', tone: 'warn',
      headline: `Current position unverified${scans}`,
      evidence, established: establishedLabel,
      explanation:
        'The most recent resolution attempt did not produce a usable position. ' +
        'The pin shows the last verified location; the device may or may not still be there.',
    };
  }

  if (attemptMs === null) {
    return {
      level: 'unknown', tone: 'muted',
      headline: 'Confirmation age unknown',
      evidence, established: establishedLabel,
      explanation:
        'This fix predates per-attempt tracking, so there is no record of when it ' +
        'was last re-confirmed. The next scan will establish one.',
    };
  }

  const attemptAgeS = (nowMs - attemptMs) / 1000;

  // ── re-affirmed recently ─────────────────────────────────────────────
  if (attemptAgeS <= STALE_ATTEMPT_S) {
    return {
      level: 'confirmed', tone: 'ok',
      headline: `Confirmed ${ago(attemptMs, nowMs)}${status ? ` · ${status}` : ''}`,
      evidence, established: establishedLabel,
      explanation:
        `The location pipeline last ran ${ago(attemptMs, nowMs)} and concluded "${status ?? 'ok'}". ` +
        'A "stationary" verdict means the device was re-checked and had not moved — ' +
        'the position is current, however old the establishing fix is.',
    };
  }

  // ── not checked lately: offline explains it, online does not ─────────
  if (online === false) {
    const lastSeenMs = lastSeen ? Date.parse(lastSeen) : NaN;
    const since = Number.isFinite(lastSeenMs)
      ? ` since ${shortDate(lastSeenMs, nowMs)}`
      : '';
    return {
      level: 'offline-stale', tone: 'muted',
      headline: `Last confirmed ${ago(attemptMs, nowMs)} · device offline${since}`,
      evidence, established: establishedLabel,
      explanation:
        'The device has been offline, so no location update was possible. A fix this ' +
        'old is the expected outcome, not a geolocation fault. The pin shows where it ' +
        'was when last heard from.',
    };
  }

  return {
    level: 'stalled', tone: 'warn',
    headline: `No location check in ${span(attemptMs, nowMs)} — device is online`,
    evidence, established: establishedLabel,
    explanation:
      'The device has been online but no location attempt has reached the cloud. ' +
      'That points at the scan pipeline on the device rather than at the position ' +
      'itself (see DE-280). The pin may be out of date.',
  };
}
